import { Add } from '@mui/icons-material'
import { Box } from '@mui/material'
import {
  LOVE_ROOT_TAG_ID,
  READINESS_ROOT_TAG_ID,
  type SectionRatings,
  splitSectionTags,
} from '@shared/tags/canonicalRatingTags'
import type { TagDefinition } from '@shared/tags/tagRegistry'
import React from 'react'

import { TagDotRow } from './TagDotRow'

interface SectionTagRailProps {
  tagIds: string[]
  tagRegistry: Record<string, TagDefinition>
  tagColorMap?: Record<string, string>
  maxCategoryDots?: number
  /** Show only the ratings pill — no category dots. Used by the TOC sidebar. */
  ratingsOnly?: boolean
  /** When provided, the rail becomes clickable (left- and right-click) and calls this on activation. */
  onRequestEdit?: (ev: React.MouseEvent) => void
}

function RatingsPill({
  ratings,
  tagRegistry,
}: {
  ratings: SectionRatings
  tagRegistry: Record<string, TagDefinition>
}) {
  const hasLove = ratings.love !== undefined
  const hasReady = ratings.readiness !== undefined
  if (!hasLove && !hasReady) {
    return null
  }
  const loveColor = tagRegistry[LOVE_ROOT_TAG_ID]?.color ?? '#e91e63'
  const readyColor = tagRegistry[READINESS_ROOT_TAG_ID]?.color ?? '#388e3c'
  const pastel = (c: string) => `color-mix(in srgb, ${c} 45%, white)`
  const title = [
    hasLove ? `שביעות רצון ${ratings.love}/5` : null,
    hasReady ? `רמת מוכנות ${ratings.readiness}/5` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Box
      title={title}
      aria-label={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: '2px',
        borderRadius: 999,
        bgcolor: 'color-mix(in srgb, var(--sidebar-text, #9e9e9e) 10%, transparent)',
        fontSize: '11px',
        fontWeight: 700,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {hasLove && <span style={{ color: pastel(loveColor) }}>{ratings.love}</span>}
      {hasLove && hasReady && <span style={{ opacity: 0.4 }}>·</span>}
      {hasReady && <span style={{ color: pastel(readyColor) }}>{ratings.readiness}</span>}
    </Box>
  )
}

/**
 * Compact read-only rail for a section's tags — digit badges for ratings + category dots.
 * Clicking (regular tap/click) invokes `onRequestEdit` so the parent can open the editor popover.
 * Long-press is reserved for drag interactions on draggable surfaces — never opens the editor.
 * When the section has no tags the rail stays invisible until hovered, then reveals a + icon.
 */
export function SectionTagRail({
  tagIds,
  tagRegistry,
  tagColorMap,
  maxCategoryDots = 2,
  ratingsOnly = false,
  onRequestEdit,
}: SectionTagRailProps) {
  const { categoryTagIds, ratings } = splitSectionTags(tagIds, tagRegistry)
  const showDots = !ratingsOnly && categoryTagIds.length > 0
  const hasAny = ratings.love !== undefined || ratings.readiness !== undefined || showDots
  const interactive = typeof onRequestEdit === 'function'

  return (
    <Box
      onClick={
        interactive
          ? (ev) => {
              ev.stopPropagation()
              onRequestEdit?.(ev)
            }
          : undefined
      }
      onContextMenu={(ev) => ev.preventDefault()}
      sx={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: interactive ? 'pointer' : 'default',
        opacity: hasAny ? 1 : 0,
        transition: 'opacity 0.15s ease',
        px: 0.25,
        '&:hover': interactive ? { opacity: 1 } : undefined,
      }}
    >
      <RatingsPill ratings={ratings} tagRegistry={tagRegistry} />
      {showDots && (
        <TagDotRow tagIds={categoryTagIds} tagRegistry={tagRegistry} tagColorMap={tagColorMap} max={maxCategoryDots} />
      )}
      {!hasAny && interactive && (
        <Add sx={{ fontSize: 12, color: 'text.secondary', opacity: 0.6 }} aria-label="Add tags" />
      )}
    </Box>
  )
}
